import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box, Container, Typography, Paper, Stack, Grid,
  FormControl, InputLabel, Select, MenuItem, TextField,
  Button, Avatar, Chip, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Tooltip, Divider, useTheme, alpha,
  Backdrop, CircularProgress, Snackbar,
  InputAdornment, Pagination, Badge, Tab, Tabs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent
} from '@mui/material';
import {
  History as HistoryIcon,
  Inventory as InventoryIcon,
  Person as PersonIcon,
  DateRange as DateRangeIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  ArrowBack as BackIcon,
  Add as AddIcon,
  RemoveCircle as RemoveIcon,
  FilterList as FilterIcon,
  ViewList as ViewListIcon,
  LocalBar as LocalBarIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useHistory } from 'react-router-dom';
import { format } from 'date-fns';

// Ionic Imports
import { IonContent, IonPage } from '@ionic/react';
import { baseUrl, config, requestID } from "../../../common/utils";

// Types for Combined Report
interface InventorySummaryItem {
  id: number;
  item_id: string;
  item_name: string;
  total_qty: number;
  action: 'add' | 'remove';
  qty_added: number;
  qty_removed: number;
  notes: string;
  date_created: string;
  createdBy: string;
}

// Types for Added Inventory
interface AddedInventoryItem {
  id: number;
  item_id: string;
  item_name: string;
  qty_added: number;
  location: string;
  notes: string;
  date_added: string;
  addedBy: string;
  reportedBy: string;
}

// Types for Removed Inventory
interface RemovedInventoryItem {
  id: number;
  item_id: string;
  item_name: string;
  qty_removed: number;
  location: string;
  notes: string;
  date_damaged: string;
  damagedBy: string;
  reportedBy: string;
}

interface ApiResponse {
  status: boolean;
  message: string;
  data: any;
}

interface FilterOptions {
  startDate: Date | null;
  endDate: Date | null;
  action: 'all' | 'add' | 'remove';
  itemId: string;
  search: string;
}

interface InventoryItem {
  item_id: string;
  item_name: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface InventoryDetails {
  inventory: any;
  additions: AddedInventoryItem[];
  removals: RemovedInventoryItem[];
}


// Tab Panel Component
const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
  </div>
);

const InventoryHistory: React.FC = () => {
  const theme = useTheme();
  const history = useHistory();
  
  const [selectedItem, setSelectedItem] = useState<{
  itemId: string;
  itemName: string;
} | null>(null);
const [itemDetails, setItemDetails] = useState<InventoryDetails | null>(null);
const [detailsLoading, setDetailsLoading] = useState<boolean>(false);
const [detailsModalOpen, setDetailsModalOpen] = useState<boolean>(false);


  // Tab state
  const [tabValue, setTabValue] = useState(0);
  
  // Data states for different tabs
  const [allTransactions, setAllTransactions] = useState<InventorySummaryItem[]>([]);
  const [addedTransactions, setAddedTransactions] = useState<AddedInventoryItem[]>([]);
  const [removedTransactions, setRemovedTransactions] = useState<RemovedInventoryItem[]>([]);
  
  // Filtered data states
  const [filteredAll, setFilteredAll] = useState<InventorySummaryItem[]>([]);
  const [filteredAdded, setFilteredAdded] = useState<AddedInventoryItem[]>([]);
  const [filteredRemoved, setFilteredRemoved] = useState<RemovedInventoryItem[]>([]);
  
  // Common states
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [showFilters, setShowFilters] = useState(true);
  const [filters, setFilters] = useState<FilterOptions>({
    startDate: new Date(new Date().setDate(new Date().getDate() - 30)),
    endDate: new Date(),
    action: 'all',
    itemId: 'all',
    search: ''
  });

const [modalStartDate, setModalStartDate] = useState<Date | null>(
  new Date(new Date().setDate(new Date().getDate() - 30))
);
const [modalEndDate, setModalEndDate] = useState<Date | null>(new Date());
const [appliedModalStartDate, setAppliedModalStartDate] = useState<Date | null>(
  new Date(new Date().setDate(new Date().getDate() - 30))
);
const [appliedModalEndDate, setAppliedModalEndDate] = useState<Date | null>(new Date());

  // Helper function to safely parse dates
const safeParseDate = (dateString: string | undefined): Date => {
  if (!dateString) return new Date(); // Return current date if undefined
  
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    console.warn('Invalid date:', dateString);
    return new Date(); 
  }
  return date;
};

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(1); // Reset page when changing tabs
  };

  // Extract unique items from all transactions for filter dropdown
  const extractInventoryItems = (data: InventorySummaryItem[]) => {
    const uniqueItems = new Map();
    data.forEach(item => {
      if (!uniqueItems.has(item.item_id)) {
        uniqueItems.set(item.item_id, {
          item_id: item.item_id,
          item_name: item.item_name
        });
      }
    });
    setInventoryItems(Array.from(uniqueItems.values()));
  };

  // Format date to YYYY-MM-DD
  const formatDate = (date: Date): string => {
    return format(date, 'yyyy-MM-dd');
  };

  // Fetch all inventory data
  const fetchAllData = async () => {
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      
      if (filters.startDate) {
        formData.append("startDate", formatDate(filters.startDate));
      }
      
      if (filters.endDate) {
        formData.append("endDate", formatDate(filters.endDate));
      }

      const response = await axios.post<ApiResponse>(
        `${baseUrl()}BeverageSupervisors/getCombinedInventoryReport`, 
        formData, 
        config
      );
      
      if (response.data.status) {
        const summaryData = response.data.data.summary || [];
        setAllTransactions(summaryData);
        extractInventoryItems(summaryData);
        applyAllFilters(summaryData);
      }
    } catch (error) {
      console.error("Fetch all inventory error:", error);
      showNotification('Failed to fetch inventory history', 'error');
    }
  };

  // Add this function to fetch item details
// Update the fetchItemDetails function
const fetchItemDetails = async (
  itemId: string, 
  itemName: string,
  useAppliedDates: boolean = true
) => {
  setDetailsLoading(true);
  setSelectedItem({ itemId, itemName });
  
  try {
    const formData = new FormData();
    formData.append("requestID", requestID());
    formData.append("item_id", itemId);
    
    // Use either applied dates or current modal dates
    const startDateToUse = useAppliedDates ? appliedModalStartDate : modalStartDate;
    const endDateToUse = useAppliedDates ? appliedModalEndDate : modalEndDate;
    
    if (startDateToUse) {
      formData.append("startDate", formatDate(startDateToUse));
    }
    
    if (endDateToUse) {
      formData.append("endDate", formatDate(endDateToUse));
    }

    const response = await axios.post<ApiResponse>(
      `${baseUrl()}BeverageSupervisors/getInventoryItemHistory`, 
      formData, 
      config
    );
    
    console.log("Item details response:", response.data);

    if (response.data.status) {
      setItemDetails(response.data.data);
      setDetailsModalOpen(true);
    } else {
      showNotification('Failed to fetch item details', 'error');
    }
  } catch (error) {
    console.error("Fetch item details error:", error);
    showNotification('Error fetching item details', 'error');
  } finally {
    setDetailsLoading(false);
  }
};

// Function to apply date filters in modal
const applyModalDateFilter = () => {
  if (selectedItem) {
    setAppliedModalStartDate(modalStartDate);
    setAppliedModalEndDate(modalEndDate);
    fetchItemDetails(selectedItem.itemId, selectedItem.itemName, true);
  }
};

// Function to reset modal date filters
const resetModalDateFilters = () => {
  const defaultStartDate = new Date(new Date().setDate(new Date().getDate() - 30));
  const defaultEndDate = new Date();
  setModalStartDate(defaultStartDate);
  setModalEndDate(defaultEndDate);
  setAppliedModalStartDate(defaultStartDate);
  setAppliedModalEndDate(defaultEndDate);
  
  if (selectedItem) {
    fetchItemDetails(selectedItem.itemId, selectedItem.itemName, true);
  }
};

const handleCloseModal = () => {
  setDetailsModalOpen(false);
  setItemDetails(null);
  setSelectedItem(null);
  // Reset modal date filters to applied state when closing
  setModalStartDate(appliedModalStartDate);
  setModalEndDate(appliedModalEndDate);
};


  // Fetch added inventory history
  const fetchAddedData = async () => {
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      
      if (filters.startDate) {
        formData.append("startDate", formatDate(filters.startDate));
      }
      
      if (filters.endDate) {
        formData.append("endDate", formatDate(filters.endDate));
      }

      const response = await axios.post<ApiResponse>(
        `${baseUrl()}BeverageSupervisors/getAddedInventoryHistory`, 
        formData, 
        config
      );
      
      if (response.data.status) {
        const addedData = response.data.data || [];
        setAddedTransactions(addedData);
        applyAddedFilters(addedData);
      }
    } catch (error) {
      console.error("Fetch added inventory error:", error);
      showNotification('Failed to fetch added inventory history', 'error');
    }
  };

  // Fetch removed inventory history
  const fetchRemovedData = async () => {
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      
      if (filters.startDate) {
        formData.append("startDate", formatDate(filters.startDate));
      }
      
      if (filters.endDate) {
        formData.append("endDate", formatDate(filters.endDate));
      }

      const response = await axios.post<ApiResponse>(
        `${baseUrl()}BeverageSupervisors/getRemovedInventoryHistory`, 
        formData, 
        config
      );
      
      if (response.data.status) {
        const removedData = response.data.data || [];
        setRemovedTransactions(removedData);
        applyRemovedFilters(removedData);
      }
    } catch (error) {
      console.error("Fetch removed inventory error:", error);
      showNotification('Failed to fetch removed inventory history', 'error');
    }
  };

  // Fetch all data based on current tab
  const fetchAllDataForTab = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchAllData(),
        fetchAddedData(),
        fetchRemovedData()
      ]);
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  // Apply filters to all transactions
  const applyAllFilters = (data: InventorySummaryItem[]) => {
    let filtered = [...data];

    if (filters.action !== 'all') {
      filtered = filtered.filter(t => t.action === filters.action);
    }

    if (filters.itemId !== 'all') {
      filtered = filtered.filter(t => t.item_id === filters.itemId);
    }

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(t => 
        t.item_name?.toLowerCase().includes(searchLower) ||
        t.notes?.toLowerCase().includes(searchLower) ||
        t.createdBy?.toLowerCase().includes(searchLower)
      );
    }

    filtered.sort((a, b) => 
  safeParseDate(b.date_created).getTime() - safeParseDate(a.date_created).getTime()
);

    setFilteredAll(filtered);
    setPage(1);
  };

// Apply filters to added transactions - UPDATE THIS
const applyAddedFilters = (data: AddedInventoryItem[]) => {
  let filtered = [...data];

  if (filters.itemId !== 'all') {
    filtered = filtered.filter(t => t.item_id === filters.itemId);
  }

  if (filters.search) {
    const searchLower = filters.search.toLowerCase();
    filtered = filtered.filter(t => 
      t.item_name?.toLowerCase().includes(searchLower) ||
      t.notes?.toLowerCase().includes(searchLower) ||
      t.addedBy?.toLowerCase().includes(searchLower) ||
      t.reportedBy?.toLowerCase().includes(searchLower)
    );
  }

  // UPDATE THIS LINE:
  filtered.sort((a, b) => 
    safeParseDate(b.date_added).getTime() - safeParseDate(a.date_added).getTime()
  );

  setFilteredAdded(filtered);
};

// Apply filters to removed transactions - UPDATE THIS
const applyRemovedFilters = (data: RemovedInventoryItem[]) => {
  let filtered = [...data];

  if (filters.itemId !== 'all') {
    filtered = filtered.filter(t => t.item_id === filters.itemId);
  }

  if (filters.search) {
    const searchLower = filters.search.toLowerCase();
    filtered = filtered.filter(t => 
      t.item_name?.toLowerCase().includes(searchLower) ||
      t.notes?.toLowerCase().includes(searchLower) ||
      t.damagedBy?.toLowerCase().includes(searchLower) ||
      t.reportedBy?.toLowerCase().includes(searchLower)
    );
  }

  // UPDATE THIS LINE:
  filtered.sort((a, b) => 
    safeParseDate(b.date_damaged).getTime() - safeParseDate(a.date_damaged).getTime()
  );

  setFilteredRemoved(filtered);
};

  // Handle filter changes
  const handleFilterChange = (field: keyof FilterOptions, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  // Apply filters when they change
  useEffect(() => {
    applyAllFilters(allTransactions);
    applyAddedFilters(addedTransactions);
    applyRemovedFilters(removedTransactions);
  }, [filters.action, filters.itemId, filters.search]);

  // Fetch data when date range changes
  useEffect(() => {
    fetchAllDataForTab();
  }, [filters.startDate, filters.endDate]);

  // Initial load
  useEffect(() => {
    fetchAllDataForTab();
  }, []);

  const showNotification = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const clearFilters = () => {
    setFilters({
      startDate: new Date(new Date().setDate(new Date().getDate() - 30)),
      endDate: new Date(),
      action: 'all',
      itemId: 'all',
      search: ''
    });
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchAllDataForTab().finally(() => setRefreshing(false));
  };

  const getTransactionChip = (transaction: InventorySummaryItem) => {
    if (transaction.action === 'add') {
      return (
        <Chip
          size="small"
          icon={<AddIcon />}
          label={`Added ${transaction.qty_added}`}
          sx={{
            bgcolor: alpha('#10b981', 0.1),
            color: '#10b981',
            fontWeight: 600
          }}
        />
      );
    } else {
      return (
        <Chip
          size="small"
          icon={<RemoveIcon />}
          label={`Removed ${transaction.qty_removed}`}
          sx={{
            bgcolor: alpha('#ef4444', 0.1),
            color: '#ef4444',
            fontWeight: 600
          }}
        />
      );
    }
  };

  const exportToCSV = () => {
    try {
      let headers: string[] = [];
      let csvData: any[][] = [];

      if (tabValue === 0) {
  headers = ['Date', 'Item', 'Current Stock', 'Created By'];
  csvData = filteredAll.map(t => [
    format(safeParseDate(t.date_created), 'yyyy-MM-dd HH:mm'),
    t.item_name,
    t.total_qty,
    t.createdBy || '-'
  ]);
} else if (tabValue === 1) {
        headers = ['Date', 'Item', 'Quantity Added', 'Location', 'Notes', 'Added By', 'Reported By'];
        csvData = filteredAdded.map(t => [
          format(safeParseDate(t.date_added), 'yyyy-MM-dd HH:mm'),
          t.item_name,
          t.qty_added,
          t.location || '-',
          t.notes || '-',
          t.addedBy || '-',
          t.reportedBy || '-'
        ]);
      } else {
        headers = ['Date', 'Item', 'Quantity Removed', 'Location', 'Notes', 'Damaged By', 'Reported By'];
        csvData = filteredRemoved.map(t => [
          format(safeParseDate(t.date_damaged), 'yyyy-MM-dd HH:mm'),
          t.item_name,
          t.qty_removed,
          t.location || '-',
          t.notes || '-',
          t.damagedBy || '-',
          t.reportedBy || '-'
        ]);
      }

      const csvContent = [
        headers.join(','),
        ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      const tabName = tabValue === 0 ? 'all' : tabValue === 1 ? 'additions' : 'removals';
      link.setAttribute('href', url);
      link.setAttribute('download', `inventory_${tabName}_${formatDate(new Date())}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      showNotification('Report exported successfully', 'success');
    } catch (error) {
      console.error('Export error:', error);
      showNotification('Failed to export report', 'error');
    }
  };

  const activeFilterCount = Object.values(filters).filter(v => v && v !== 'all' && v !== '').length;

  // Pagination
  const getCurrentData = () => {
    if (tabValue === 0) return filteredAll;
    if (tabValue === 1) return filteredAdded;
    return filteredRemoved;
  };

  const currentData = getCurrentData();
  const pageCount = Math.ceil(currentData.length / rowsPerPage);
  const paginatedData = currentData.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const getDateRangeLabel = () => {
    if (filters.startDate && filters.endDate) {
      return `${formatDate(filters.startDate)} to ${formatDate(filters.endDate)}`;
    }
    return 'Select date range';
  };

  // Calculate statistics based on current tab
const getStats = () => {
  if (tabValue === 0) {
    // For All Transactions tab
    // Get unique items count from allTransactions (distinct item_id)
    const uniqueItems = new Set(allTransactions.map(t => t.item_id));
    const uniqueItemsCount = uniqueItems.size;
    
    // Get additions count and total units from addedTransactions (unfiltered)
    const additionsCount = addedTransactions.length;
    const totalAdditions = addedTransactions.reduce((sum, t) => sum + t.qty_added, 0);
    
    // Get removals count and total units from removedTransactions (unfiltered)
    const removalsCount = removedTransactions.length;
    const totalRemovals = removedTransactions.reduce((sum, t) => sum + t.qty_removed, 0);

    return {
      total: uniqueItemsCount, // Number of unique items (not units)
      additions: additionsCount,
      removals: removalsCount,
      totalAdditions,
      totalRemovals
    };
  } else if (tabValue === 1) {
    // For Additions Only tab - use filteredAdded (respects filters)
    return {
      total: filteredAdded.length,
      additions: filteredAdded.length,
      removals: 0,
      totalAdditions: filteredAdded.reduce((sum, t) => sum + t.qty_added, 0),
      totalRemovals: 0
    };
  } else {
    // For Removals Only tab - use filteredRemoved (respects filters)
    return {
      total: filteredRemoved.length,
      additions: 0,
      removals: filteredRemoved.length,
      totalAdditions: 0,
      totalRemovals: filteredRemoved.reduce((sum, t) => sum + t.qty_removed, 0)
    };
  }
};
  

  const stats = getStats();

  return (
    <IonPage>
      <IonContent>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box sx={{ bgcolor: theme.palette.background.default, minHeight: '100vh' }}>
            
            {/* Header */}
            <Paper 
              elevation={0}
              sx={{ 
                borderBottom: `1px solid ${theme.palette.divider}`,
                position: 'sticky',
                top: 0,
                zIndex: 10,
                bgcolor: 'background.paper'
              }}
            >
              <Container maxWidth="xl">
                <Stack direction="row" alignItems="center" spacing={2} sx={{ py: 2 }}>
                  <IconButton onClick={() => history.goBack()}>
                    <BackIcon />
                  </IconButton>
                  <Avatar
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main
                    }}
                  >
                    <HistoryIcon />
                  </Avatar>
                  <Box flex={1}>
                    <Typography variant="h5" fontWeight={600}>
                      Inventory History
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Track all inventory additions and removals
                    </Typography>
                  </Box>
                  <Chip 
                    label={getDateRangeLabel()}
                    icon={<DateRangeIcon />}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                  <Tooltip title="Export to CSV">
                    <span>
                      <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={exportToCSV}
                        disabled={currentData.length === 0}
                        size="small"
                      >
                        Export
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip title="Refresh">
                    <Button
                      variant="contained"
                      startIcon={<RefreshIcon />}
                      onClick={handleRefresh}
                      disabled={refreshing}
                      size="small"
                    >
                      Refresh
                    </Button>
                  </Tooltip>
                </Stack>

                {/* Tabs */}
                <Tabs 
                  value={tabValue} 
                  onChange={handleTabChange}
                  variant="fullWidth"
                  sx={{ borderBottom: 1, borderColor: 'divider' }}
                >
                  <Tab 
                    icon={<ViewListIcon />} 
                    label="All Transactions" 
                    iconPosition="start"
                  />
                  <Tab 
                    icon={<AddIcon />} 
                    label="Additions Only" 
                    iconPosition="start"
                    sx={{ color: '#10b981' }}
                  />
                  <Tab 
                    icon={<RemoveIcon />} 
                    label="Removals Only" 
                    iconPosition="start"
                    sx={{ color: '#ef4444' }}
                  />
                </Tabs>
              </Container>
            </Paper>

            <Container maxWidth="xl" sx={{ py: 3 }}>
              
              {/* Stats Cards */}
{/* Stats Cards */}
<Grid container spacing={3} sx={{ mb: 3 }}>
  {/* First Card - Always show but with different labels */}
  <Grid item xs={12} sm={6} md={3}>
    <Paper sx={{ p: 2 }}>
      <Typography color="text.secondary" variant="body2">
        {tabValue === 0 ? 'Available Stock Items' : 
         tabValue === 1 ? 'Total Additions' : 'Total Removals'}
      </Typography>
      <Typography variant="h4">{stats.total}</Typography>
    </Paper>
  </Grid>
  
  {/* Additions Card - Hide in Removals tab */}
  {tabValue !== 2 && (
    <Grid item xs={12} sm={6} md={3}>
      <Paper sx={{ p: 2, bgcolor: alpha('#10b981', 0.05) }}>
        <Typography color="text.secondary" variant="body2">
          {tabValue === 0 ? 'Additions Count' : 'Additions'}
        </Typography>
        <Typography variant="h4" color="#10b981">{stats.additions}</Typography>
        <Typography variant="caption">Total Units: {stats.totalAdditions}</Typography>
      </Paper>
    </Grid>
  )}
  
  {/* Removals Card - Hide in Additions tab */}
  {tabValue !== 1 && (
    <Grid item xs={12} sm={6} md={3}>
      <Paper sx={{ p: 2, bgcolor: alpha('#ef4444', 0.05) }}>
        <Typography color="text.secondary" variant="body2">
          {tabValue === 0 ? 'Removals Count' : 'Removals'}
        </Typography>
        <Typography variant="h4" color="#ef4444">{stats.removals}</Typography>
        <Typography variant="caption">Total Units: {stats.totalRemovals}</Typography>
      </Paper>
    </Grid>
  )}
  
  {/* Net Change/Total Stock Card - Always show */}
  <Grid item xs={12} sm={6} md={3}>
    <Paper sx={{ p: 2 }}>
      <Typography color="text.secondary" variant="body2">
        {tabValue === 0 ? 'Total Stock Units' : 'Net Change'}
      </Typography>
      <Typography 
        variant="h4" 
        color={stats.totalAdditions - stats.totalRemovals >= 0 ? '#10b981' : '#ef4444'}
      >
        {stats.totalAdditions - stats.totalRemovals}
      </Typography>
    </Paper>
  </Grid>
</Grid>

              {/* Filters */}
              <Paper sx={{ mb: 3, overflow: 'hidden' }}>
                <Box sx={{ p: 2, bgcolor: theme.palette.background.paper }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Badge badgeContent={activeFilterCount} color="primary">
                        <FilterIcon color="action" />
                      </Badge>
                      <Typography variant="subtitle1" fontWeight={600}>
                        Filters
                      </Typography>
                    </Stack>
                    <Button
                      size="small"
                      onClick={() => setShowFilters(!showFilters)}
                    >
                      {showFilters ? 'Hide Filters' : 'Show Filters'}
                    </Button>
                  </Stack>
                </Box>

                {showFilters && (
                  <>
                    <Divider />
                    <Box sx={{ p: 2 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={3}>
                          <DatePicker
                            label="Start Date"
                            value={filters.startDate}
                            onChange={(date) => handleFilterChange('startDate', date)}
                            slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <DatePicker
                            label="End Date"
                            value={filters.endDate}
                            onChange={(date) => handleFilterChange('endDate', date)}
                            slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                          />
                        </Grid>
                        
                        {/* Action filter - only show for All Transactions tab */}
                        {/* {tabValue === 0 && (
                          <Grid item xs={12} md={2}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Action</InputLabel>
                              <Select
                                value={filters.action}
                                label="Action"
                                onChange={(e) => handleFilterChange('action', e.target.value)}
                              >
                                <MenuItem value="all">All Actions</MenuItem>
                                <MenuItem value="add">Additions</MenuItem>
                                <MenuItem value="remove">Removals</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                        )} */}

                        <Grid item xs={12} md={tabValue === 0 ? 2 : 3}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Item</InputLabel>
                            <Select
                              value={filters.itemId}
                              label="Item"
                              onChange={(e) => handleFilterChange('itemId', e.target.value)}
                            >
                              <MenuItem value="all">All Items</MenuItem>
                              {inventoryItems.map(item => (
                                <MenuItem key={item.item_id} value={item.item_id}>
                                  {item.item_name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} md={tabValue === 0 ? 2 : 3}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Search"
                            value={filters.search}
                            onChange={(e) => handleFilterChange('search', e.target.value)}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon fontSize="small" />
                                </InputAdornment>
                              ),
                              endAdornment: filters.search && (
                                <InputAdornment position="end">
                                  <IconButton 
                                    size="small" 
                                    onClick={() => handleFilterChange('search', '')}
                                  >
                                    <ClearIcon fontSize="small" />
                                  </IconButton>
                                </InputAdornment>
                              )
                            }}
                          />
                        </Grid>
                      </Grid>

                      <Stack direction="row" justifyContent="space-between" sx={{ mt: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          Showing {currentData.length} transactions
                        </Typography>
                        <Button
                          size="small"
                          onClick={clearFilters}
                          startIcon={<ClearIcon />}
                        >
                          Clear All
                        </Button>
                      </Stack>
                    </Box>
                  </>
                )}
              </Paper>

              {/* All Transactions Tab */}
<TabPanel value={tabValue} index={0}>
  <Paper>
    <TableContainer>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Date & Time</TableCell>
            <TableCell>Item</TableCell>
            <TableCell>Current Stock</TableCell>
            <TableCell>Created By</TableCell>
            <TableCell align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                <CircularProgress />
              </TableCell>
            </TableRow>
          ) : paginatedData.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                <Stack spacing={2} alignItems="center">
                  <InventoryIcon sx={{ fontSize: 48, color: theme.palette.grey[300] }} />
                  <Typography color="text.secondary">No transactions found</Typography>
                  <Button variant="outlined" size="small" onClick={clearFilters}>
                    Clear Filters
                  </Button>
                </Stack>
              </TableCell>
            </TableRow>
          ) : (
            (paginatedData as InventorySummaryItem[]).map((transaction) => (
              <TableRow key={`all-${transaction.id}`} hover>
                <TableCell>
                  <Typography variant="body2">
                    {format(safeParseDate(transaction.date_created), 'MMM dd, yyyy HH:mm')}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>
                    {transaction.item_name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={transaction.total_qty}
                    size="small"
                    color="primary"
                    variant="outlined"
                    icon={<InventoryIcon />}
                  />
                </TableCell>
                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Avatar sx={{ width: 24, height: 24, fontSize: 12 }}>
                      {transaction.createdBy?.charAt(0) || '?'}
                    </Avatar>
                    <Typography variant="body2">
                      {transaction.createdBy || 'Unknown'}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="View Item History">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => fetchItemDetails(transaction.item_id, transaction.item_name, true)}
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  </Paper>
</TabPanel>

              {/* Additions Only Tab */}
<TabPanel value={tabValue} index={1}>
  <Paper>
    <TableContainer>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Date & Time</TableCell>
            <TableCell>Item</TableCell>
            <TableCell>Quantity Added</TableCell>
            <TableCell>Location</TableCell>
            <TableCell>Notes</TableCell>
            <TableCell>Added By</TableCell>
            <TableCell>Reported By</TableCell>
            <TableCell align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                <CircularProgress />
              </TableCell>
            </TableRow>
          ) : paginatedData.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                <Stack spacing={2} alignItems="center">
                  <AddIcon sx={{ fontSize: 48, color: theme.palette.grey[300] }} />
                  <Typography color="text.secondary">No additions found</Typography>
                  <Button variant="outlined" size="small" onClick={clearFilters}>
                    Clear Filters
                  </Button>
                </Stack>
              </TableCell>
            </TableRow>
          ) : (
            (paginatedData as AddedInventoryItem[]).map((transaction) => (
              <TableRow key={`add-${transaction.id}`} hover>
                <TableCell>
                  <Typography variant="body2">
                    {format(safeParseDate(transaction.date_added), 'MMM dd, yyyy HH:mm')}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>
                    {transaction.item_name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={transaction.qty_added}
                    size="small"
                    color="success"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {transaction.location || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 200 }}>
                    {transaction.notes || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Avatar sx={{ width: 24, height: 24, fontSize: 12 }}>
                      {transaction.addedBy?.charAt(0) || '?'}
                    </Avatar>
                    <Typography variant="body2">
                      {transaction.addedBy || 'Unknown'}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {transaction.reportedBy || '-'}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="View Item History">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => fetchItemDetails(transaction.item_id, transaction.item_name, true)}
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  </Paper>
</TabPanel>

              {/* Removals Only Tab */}
              {/* Removals Only Tab */}
<TabPanel value={tabValue} index={2}>
  <Paper>
    <TableContainer>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Date & Time</TableCell>
            <TableCell>Item</TableCell>
            <TableCell>Quantity Removed</TableCell>
            <TableCell>Location</TableCell>
            <TableCell>Notes</TableCell>
            <TableCell>Damaged By</TableCell>
            <TableCell>Reported By</TableCell>
            <TableCell align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                <CircularProgress />
              </TableCell>
            </TableRow>
          ) : paginatedData.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                <Stack spacing={2} alignItems="center">
                  <RemoveIcon sx={{ fontSize: 48, color: theme.palette.grey[300] }} />
                  <Typography color="text.secondary">No removals found</Typography>
                  <Button variant="outlined" size="small" onClick={clearFilters}>
                    Clear Filters
                  </Button>
                </Stack>
              </TableCell>
            </TableRow>
          ) : (
            (paginatedData as RemovedInventoryItem[]).map((transaction) => (
              <TableRow key={`remove-${transaction.id}`} hover>
                <TableCell>
                  <Typography variant="body2">
                    {format(safeParseDate(transaction.date_damaged), 'MMM dd, yyyy HH:mm')}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>
                    {transaction.item_name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={transaction.qty_removed}
                    size="small"
                    color="error"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {transaction.location || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 200 }}>
                    {transaction.notes || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Avatar sx={{ width: 24, height: 24, fontSize: 12 }}>
                      {transaction.damagedBy?.charAt(0) || '?'}
                    </Avatar>
                    <Typography variant="body2">
                      {transaction.damagedBy || 'Unknown'}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {transaction.reportedBy || '-'}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="View Item History">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => fetchItemDetails(transaction.item_id, transaction.item_name, true)}
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  </Paper>
</TabPanel>

              {/* Pagination */}
              {currentData.length > 0 && (
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                  <Pagination
                    count={pageCount}
                    page={page}
                    onChange={(e, value) => setPage(value)}
                    color="primary"
                    size="small"
                  />
                </Box>
              )}

              {/* Item Details Modal */}
{/* Item Details Modal */}
<Dialog
  open={detailsModalOpen}
  onClose={handleCloseModal}
  maxWidth="lg"
  fullWidth
>
  <DialogTitle>
    <Stack direction="row" alignItems="center" spacing={1}>
      <InventoryIcon color="primary" />
      <Typography variant="h6">
        Item History: {selectedItem?.itemName}
      </Typography>
    </Stack>
  </DialogTitle>
  <DialogContent dividers>
    {/* Date Filter Section Inside Modal */}
    <Paper sx={{ p: 2, mb: 3, bgcolor: alpha(theme.palette.primary.main, 0.03) }}>
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        Filter by Date Range
      </Typography>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={4}>
          <DatePicker
            label="Start Date"
            value={modalStartDate}
            onChange={(date) => setModalStartDate(date)}
            slotProps={{ textField: { fullWidth: true, size: 'small' } }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <DatePicker
            label="End Date"
            value={modalEndDate}
            onChange={(date) => setModalEndDate(date)}
            slotProps={{ textField: { fullWidth: true, size: 'small' } }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              size="small"
              onClick={applyModalDateFilter}
              startIcon={<DateRangeIcon />}
            >
              Apply Filter
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={resetModalDateFilters}
              startIcon={<ClearIcon />}
            >
              Reset
            </Button>
          </Stack>
        </Grid>
      </Grid>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        Showing data from {appliedModalStartDate ? formatDate(appliedModalStartDate) : 'start'} to {appliedModalEndDate ? formatDate(appliedModalEndDate) : 'end'}
      </Typography>
    </Paper>

    {detailsLoading ? (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    ) : itemDetails ? (
      <Stack spacing={3}>
        {/* Current Stock Info */}
        {itemDetails.inventory && (
          <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Current Stock Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Item ID</Typography>
                <Typography variant="body1">{itemDetails.inventory.item_id}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Item Name</Typography>
                <Typography variant="body1">{itemDetails.inventory.item_name}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Total Quantity</Typography>
                <Typography variant="h5" color="primary">{itemDetails.inventory.total_qty}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Last Updated</Typography>
                <Typography variant="body1">
                  {itemDetails.inventory.date_created 
                    ? format(safeParseDate(itemDetails.inventory.date_created), 'MMM dd, yyyy HH:mm')
                    : 'N/A'}
                </Typography>
              </Grid>
              {itemDetails.inventory.notes && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Notes</Typography>
                  <Typography variant="body1">{itemDetails.inventory.notes}</Typography>
                </Grid>
              )}
            </Grid>
          </Paper>
        )}

        {/* Additions History */}
        {itemDetails.additions && itemDetails.additions.length > 0 ? (
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ color: '#10b981' }}>
              <AddIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Additions History ({itemDetails.additions.length})
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date & Time</TableCell>
                    <TableCell>Quantity</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Added By</TableCell>
                    <TableCell>Reported By</TableCell>
                    <TableCell>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {itemDetails.additions.map((add) => (
                    <TableRow key={`modal-add-${add.id}`} hover>
                      <TableCell>
                        {format(safeParseDate(add.date_added), 'MMM dd, yyyy HH:mm')}
                      </TableCell>
                      <TableCell>
                        <Chip label={add.qty_added} size="small" color="success" variant="outlined" />
                      </TableCell>
                      <TableCell>{add.location || '-'}</TableCell>
                      <TableCell>{add.addedBy || '-'}</TableCell>
                      <TableCell>{add.reportedBy || '-'}</TableCell>
                      <TableCell>{add.notes || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ) : (
          <Alert severity="info" icon={<AddIcon />}>
            No additions found for this date range
          </Alert>
        )}

        {/* Removals History */}
        {itemDetails.removals && itemDetails.removals.length > 0 ? (
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ color: '#ef4444' }}>
              <RemoveIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Removals History ({itemDetails.removals.length})
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date & Time</TableCell>
                    <TableCell>Quantity</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Damaged By</TableCell>
                    <TableCell>Reported By</TableCell>
                    <TableCell>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {itemDetails.removals.map((remove) => (
                    <TableRow key={`modal-remove-${remove.id}`} hover>
                      <TableCell>
                        {format(safeParseDate(remove.date_damaged), 'MMM dd, yyyy HH:mm')}
                      </TableCell>
                      <TableCell>
                        <Chip label={remove.qty_removed} size="small" color="error" variant="outlined" />
                      </TableCell>
                      <TableCell>{remove.location || '-'}</TableCell>
                      <TableCell>{remove.damagedBy || '-'}</TableCell>
                      <TableCell>{remove.reportedBy || '-'}</TableCell>
                      <TableCell>{remove.notes || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ) : (
          <Alert severity="info" icon={<RemoveIcon />}>
            No removals found for this date range
          </Alert>
        )}
      </Stack>
    ) : (
      <Alert severity="error">Failed to load item details</Alert>
    )}
  </DialogContent>
  <DialogActions>
    <Button onClick={handleCloseModal}>Close</Button>
  </DialogActions>
</Dialog>
            </Container>

            {/* Refresh Indicator */}
            {refreshing && (
              <Box
                sx={{
                  position: 'fixed',
                  top: 80,
                  right: 24,
                  zIndex: 1100
                }}
              >
                <Paper sx={{ p: 1, bgcolor: alpha(theme.palette.primary.main, 0.9), color: 'white' }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={16} color="inherit" />
                    <Typography variant="caption">Refreshing...</Typography>
                  </Stack>
                </Paper>
              </Box>
            )}

            {/* Snackbar */}
            <Snackbar
              open={snackbar.open}
              autoHideDuration={4000}
              onClose={handleCloseSnackbar}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled">
                {snackbar.message}
              </Alert>
            </Snackbar>

          </Box>
        </LocalizationProvider>
      </IonContent>
    </IonPage>
  );
};

export default InventoryHistory;